<template>
    <div
        id="eloomi-main-layout"
        class="core-main-layout"
        :class="{
            'has-left-sidebar': canShowLeftSidebar,
            'left-sidebar-is-compact': canShowLeftSidebar && leftSidebarIsCompact,
        }"
    >
        <div class="sticky-header">
            <portal-target name="sticky-header" />
            <slot name="sticky-header" />
        </div>
        <slot name="header"></slot>

        <div class="content-slot">
            <div v-if="$scopedSlots['left-sidebar']" v-show="hasLeftSidebar" class="left-sidebar-slot">
                <slot name="left-sidebar" :canBeCollapsed="canBeCollapsed"></slot>
            </div>
            <slot name="content"></slot>
        </div>

        <slot name="overlay"></slot>
    </div>
</template>

<script lang="ts">
    import { Component, Mixins, Prop } from 'vue-property-decorator';
    import { getMaxWidthMedia } from '@/common/services';
    import { MobileModeStoreMixin } from '@/common/store/mobile-mode';
    import { LaunchDarklyFeaturesMappedStore } from '@/launch-darkly/store';

    @Component({})
    export default class CoreMainLayout extends Mixins(MobileModeStoreMixin, LaunchDarklyFeaturesMappedStore) {
        @Prop({ default: false, type: Boolean }) public hasLeftSidebar!: boolean;
        @Prop({ default: false, type: Boolean }) public leftSidebarIsCompact!: boolean;

        public mql = window.matchMedia(getMaxWidthMedia(1424));

        public canBeCollapsed = false;

        public mounted() {
            this.canBeCollapsed = this.mql.matches;
            if (this.mql.addEventListener) {
                this.mql.addEventListener('change', this.handleMqlChangeEvent);
            }
        }

        public destroyed() {
            if (this.mql.removeEventListener) {
                this.mql.removeEventListener('change', this.handleMqlChangeEvent);
            }
        }

        public handleMqlChangeEvent(event: MediaQueryListEvent) {
            this.canBeCollapsed = event.matches;
        }

        public get canShowLeftSidebar() {
            return this.$scopedSlots['left-sidebar'] && this.hasLeftSidebar;
        }
    }
</script>

<style lang="less" scoped>
    @left-sidebar-width: @spacing-96 * 4;
    @left-sidebar-width-collapsed: 120px;

    .expanded-sidebar-style() {
        width: @left-sidebar-width;

        &::before {
            .animated-visible();
        }
    }

    .expanded-sidebar-style(withShadow) {
        .expanded-sidebar-style();

        box-shadow: 0 2px 40px 0 rgba(1, 13, 32, 0.08);
    }

    .core-main-layout {
        display: flex;
        flex: 1;
        flex-direction: column;
        width: 100%;
        max-width: 100%;

        &.has-left-sidebar {
            .content-slot {
                margin-left: @left-sidebar-width-collapsed;
            }
        }

        &.has-left-sidebar:not(.left-sidebar-is-compact) {
            .content-slot {
                .xl-viewport-and-larger({
                    margin-left: @left-sidebar-width;
                }, 200);
            }
        }

        .left-sidebar-slot {
            position: fixed;
            left: 0;
            z-index: 2;
            width: @left-sidebar-width-collapsed;
            height: 100%;
            overflow-y: auto;
            background: linear-gradient(@bright-color, @core-background-color);

            // We need to smoothly animate linear-gradient → bright-color fill transition
            // But gradients have no transitions yet
            // So we overlapping it with smoothly fading bg div
            &::before {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                background-color: @bright-color;
                content: '';
                pointer-events: none;

                .animated-hidden();
            }
            .generic-transition(~'width, box-shadow');
        }

        &:not(.left-sidebar-is-compact) {
            .left-sidebar-slot {
                .lg-viewport-and-smaller({
                    &:hover {
                        .expanded-sidebar-style(withShadow);
                    }
                }, 200);

                .xl-viewport-and-larger({
                    .expanded-sidebar-style();
                }, 200);
            }
        }

        &.left-sidebar-is-compact .left-sidebar-slot:hover {
            .expanded-sidebar-style(withShadow);
        }

        .content-slot {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            .generic-transition(margin);
        }
    }
</style>
